import type { SalesFunnelStage } from '@/types/sales-performance.types';
import { count, percent } from './format';
import { buildSankeyLayout, NODE_WIDTH, type SankeyNode, type SankeyVariant } from './sankey-layout';
import { useElementWidth } from './use-element-width';

/** Figma Kim-AI 6631:79557 / 6701:27790 — the funnel drawn as a Sankey. */

const MIN_WIDTH = 760;

function NodeLabel({ node, x, tone, testId }: { node: SankeyNode; x: number; tone: string; testId: string }) {
  return <div data-testid={testId} className='absolute flex flex-col overflow-clip whitespace-nowrap' style={{ left: x, top: node.labelTop }}>
    <span className='flex items-center gap-1'>
      <span className='text-xs font-semibold leading-4 text-ink-strong'>{count(node.count)}</span>
      <span className='text-[11px] leading-4 text-gray-900'>/ {percent(node.share)}</span>
    </span>
    <span className={`text-[11px] leading-4 ${tone}`}>{node.label}</span>
  </div>;
}

interface PerformanceSankeyProps { stages: SalesFunnelStage[]; title: string; variant: SankeyVariant; hideEntry: boolean }

export function PerformanceSankey({ stages, title, variant, hideEntry }: PerformanceSankeyProps) {
  const [container, measured] = useElementWidth<HTMLDivElement>(1150);
  const width = Math.max(MIN_WIDTH, measured);
  const layout = buildSankeyLayout(stages, width, variant, hideEntry);
  const summary = `${title}: ${stages.map((stage) => `${stage.label} ${count(stage.count)}`).join(', ')}`;
  return <div ref={container} className='w-full overflow-x-auto'>
    <div role='img' aria-label={summary} className='relative' style={{ width, height: variant.chartHeight }}>
      <svg aria-hidden className='absolute inset-0' width={width} height={variant.chartHeight}>
        {layout.flows.map((flow) => <path key={flow.key} d={flow.path} fill={flow.fill} opacity={flow.opacity} />)}
      </svg>
      {layout.tail && <div aria-hidden className='absolute top-0 rounded-r-md bg-indigo-200 opacity-[0.18]' style={{ left: layout.tail.x, width: layout.tail.width, height: layout.tail.height }} />}
      {layout.columns.map((column, index) => <div key={column.key}>
        <div data-testid={`node-${column.key}`} className='absolute rounded-[3px]' style={{ left: column.x, top: 0, width: NODE_WIDTH, height: column.retained.height, background: `var(--color-${column.retained.color})` }} />
        {column.dropped && <div data-testid={`drop-${column.key}`} className='absolute rounded-[3px]' style={{ left: column.x, top: column.dropped.top, width: NODE_WIDTH, height: column.dropped.height, background: `var(--color-${column.dropped.color})` }} />}
        {/* The first column's label sits inside its outgoing band, the design's reading start. */}
        <NodeLabel testId={`label-${column.key}`} node={column.retained} x={column.labelX} tone={index === 0 ? 'text-gray-900' : variant.labelTone} />
        {column.dropped && <NodeLabel testId={`drop-label-${column.key}`} node={column.dropped} x={column.labelX} tone={variant.labelTone} />}
      </div>)}
    </div>
  </div>;
}
